import React from 'react';
import { CalendarDaysIcon } from '@heroicons/react/24/outline';
import { useDriverSchedule } from '../../hooks/useDriverSchedule';
import { formatDateForDisplay } from '../../utils/dateUtils';
import { SCHEDULE_CONSTANTS, DASHBOARD_STATS_LABELS } from '../../constants/dashboard';
import { HighlightCaseCard } from './HighlightCaseCard';
import { CaseTimelineCard } from './CaseTimelineCard';

interface DriverScheduleViewProps {
  selectedDate: string;
  onCaseStatusUpdate?: (caseId: string, newStatus: 'in_transit' | 'completed') => void;
  viewMode?: 'driver' | 'admin';
}

/**
 * 陸送員専用スケジュール表示コンポーネント
 * モバイルファーストで使いやすい陸送員向けUI
 * 管理者モードでは全案件を表示
 */
export const DriverScheduleView: React.FC<DriverScheduleViewProps> = ({
  selectedDate,
  onCaseStatusUpdate,
  viewMode = 'driver',
}) => {
  const { todaysCases, nextCase, currentCase, scheduleAnalysis, isEmpty } = useDriverSchedule(
    selectedDate,
    viewMode
  );

  // 空のスケジュールの場合
  if (isEmpty) {
    return (
      <div className='text-center py-12'>
        <CalendarDaysIcon className='mx-auto h-16 w-16 text-gray-400 dark:text-gray-600 mb-4' />
        <h3 className='text-lg font-medium text-gray-900 dark:text-white mb-2'>
          {formatDateForDisplay(selectedDate)}のスケジュールはありません
        </h3>
        <p className='text-gray-500 dark:text-gray-400'>
          {SCHEDULE_CONSTANTS.EMPTY_SCHEDULE_MESSAGE}
        </p>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      {/* 進捗サマリー - 管理者モードのみ */}
      {viewMode === 'admin' && (
        <div className='bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4'>
          <div className='flex items-center justify-between mb-3'>
            <h2 className='text-lg font-semibold text-gray-900 dark:text-white'>
              {formatDateForDisplay(selectedDate)}の進捗
            </h2>
            <div className='text-right'>
              <div className='text-2xl font-bold text-blue-600 dark:text-blue-400'>
                {scheduleAnalysis.completionRate}%
              </div>
              <div className='text-xs text-gray-500 dark:text-gray-400'>
                {DASHBOARD_STATS_LABELS.COMPLETION_RATE}
              </div>
            </div>
          </div>

          <div className='grid grid-cols-3 gap-4 text-center'>
            <div>
              <div className='text-lg font-semibold text-green-600 dark:text-green-400'>
                {scheduleAnalysis.completed}
              </div>
              <div className='text-xs text-gray-500 dark:text-gray-400'>
                {DASHBOARD_STATS_LABELS.COMPLETED}
              </div>
            </div>
            <div>
              <div className='text-lg font-semibold text-yellow-600 dark:text-yellow-400'>
                {scheduleAnalysis.inTransit}
              </div>
              <div className='text-xs text-gray-500 dark:text-gray-400'>
                {DASHBOARD_STATS_LABELS.IN_TRANSIT}
              </div>
            </div>
            <div>
              <div className='text-lg font-semibold text-blue-600 dark:text-blue-400'>
                {scheduleAnalysis.assigned}
              </div>
              <div className='text-xs text-gray-500 dark:text-gray-400'>
                {DASHBOARD_STATS_LABELS.ASSIGNED}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 次の案件・現在の案件のハイライト - 管理者モードのみ */}
      {viewMode === 'admin' && (nextCase || currentCase) && (
        <div className='space-y-4'>
          {currentCase && (
            <HighlightCaseCard
              case={currentCase}
              type='current'
              onStatusUpdate={onCaseStatusUpdate}
            />
          )}
          {nextCase && (
            <HighlightCaseCard case={nextCase} type='next' onStatusUpdate={onCaseStatusUpdate} />
          )}
        </div>
      )}

      {/* 全案件リスト */}
      <div className='space-y-3'>
        <h3 className='text-lg font-medium text-gray-900 dark:text-white'>
          本日のスケジュール（{todaysCases.length}件）
        </h3>
        {todaysCases.map(case_ => (
          <CaseTimelineCard key={case_.id} case={case_} onStatusUpdate={onCaseStatusUpdate} />
        ))}
      </div>
    </div>
  );
};
